// Inicio tipo kanban (v5.0.0): 4 columnas (Administración · Diagnóstico ·
// Clases en vivo · Tecnología) con las aplicaciones como iconos de celular/tablet.
import { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, X, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { NAVS, visibleChildren } from "@/lib/navConfig";
import { OWL_CLASSIC } from "@/lib/avatars";
import { APP_VERSION } from "@/lib/version";
import { speakOwl, stopOwl, isOwlVoiceOn, setOwlVoice, owlVoiceSupported, defaultOwlVoiceForStudents } from "@/lib/owlVoice";

const ROLE_LABEL = { admin: "Administrador", franquiciado: "Franquiciado", docente: "Docente", estudiante: "Estudiante" };

function AppTile({ item, onSpeak }) {
  const Icon = item.icon;
  const inner = (
    <>
      <span
        className="neo-app-icon"
        style={{ background: `linear-gradient(145deg, ${item.color[0]}, ${item.color[1]})`, boxShadow: `0 10px 18px -6px ${item.color[1]}99, inset 2px 2px 4px rgba(255,255,255,.35)` }}
      >
        <Icon size={28} strokeWidth={2.1} color="#fff" />
      </span>
      <span className="mt-2 text-[12px] leading-tight font-extrabold text-[#1F2547] text-center break-words">{item.label}</span>
      {item.soon && <span className="mt-1 text-[9px] font-extrabold uppercase bg-[#FFF7DA] text-[#9C7A18] rounded px-1.5 py-0.5">Próximamente</span>}
    </>
  );
  if (item.soon) {
    return <div data-testid={`hub-${item.testid}`} className="neo-app opacity-60 cursor-not-allowed">{inner}</div>;
  }
  return (
    <Link
      to={item.to} data-testid={`hub-${item.testid}`} className="neo-app" title={item.label}
      onMouseEnter={onSpeak ? () => onSpeak(item.label) : undefined}
      onFocus={onSpeak ? () => onSpeak(item.label) : undefined}
      onTouchStart={onSpeak ? () => onSpeak(item.label) : undefined}
    >
      {inner}
    </Link>
  );
}

export default function AppHub() {
  const { user } = useAuth();
  const modules = user?.effective_modules || [];
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  // v5.6.4 · menú del ESTUDIANTE con sonido: Zarek dice el nombre de cada aplicación al pasar/tocar,
  // y hay un botón para activar o silenciar la voz (por defecto activada para estudiantes).
  const isStudent = user?.role === "estudiante";
  const [voiceOn, setVoiceOn] = useState(() => (isStudent ? defaultOwlVoiceForStudents() : isOwlVoiceOn()));
  const speakTimer = useRef(null);
  const speakTile = (label) => {
    if (!isStudent || !isOwlVoiceOn()) return;
    clearTimeout(speakTimer.current);
    speakTimer.current = setTimeout(() => speakOwl(label), 250);
  };
  const toggleVoice = () => {
    const next = !voiceOn;
    setOwlVoice(next);
    setVoiceOn(next);
    if (next) speakOwl("¡Hola! Soy Zarek. Toca una aplicación y te digo cómo se llama.", { force: true });
    else stopOwl();
  };
  useEffect(() => () => { clearTimeout(speakTimer.current); stopOwl(); }, []);
  // Atajo: «/» enfoca el buscador de aplicaciones.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) {
        e.preventDefault(); inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const norm = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const allGroups = (NAVS[user?.role] || [])
    .map((g) => ({ ...g, items: visibleChildren(g, modules, !!user?.is_super_admin) }))
    .filter((g) => g.items.length);
  const groups = useMemo(() => {
    const nq = norm(q).trim();
    if (!nq) return allGroups;
    return allGroups
      .map((g) => ({ ...g, items: g.items.filter((it) => norm(it.label).includes(nq) || norm(g.label).includes(nq)) }))
      .filter((g) => g.items.length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, user?.role, modules.join(","), user?.is_super_admin]);
  const totalFound = groups.reduce((n, g) => n + g.items.length, 0);
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";
  const first = (user?.name || user?.full_name || "").split(" ")[0];

  return (
    <div className="space-y-7" data-testid="app-hub">
      <div className="neo-card p-5 sm:p-6 flex items-center gap-4">
        <img src={OWL_CLASSIC} alt="Zarek" className="w-16 h-16 object-contain shrink-0" draggable={false} />
        <div className="min-w-0">
          <p className="text-[11px] tracking-[0.2em] uppercase font-extrabold text-[#A43790]">{ROLE_LABEL[user?.role] || "Panel"}</p>
          <h1 className="font-brand text-2xl sm:text-3xl text-[#1F2547] leading-tight">
            {greet}{first ? `, ${first}` : ""}
          </h1>
          <p className="text-sm text-[#6E7591]">Elige una aplicación para empezar.</p>
        </div>
        {isStudent && owlVoiceSupported() && (
          <button type="button" onClick={toggleVoice} data-testid="hub-voice-toggle"
            title={voiceOn ? "Silenciar la voz de Zarek" : "Activar la voz de Zarek"}
            aria-pressed={voiceOn}
            className={`ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-extrabold ${voiceOn ? "bg-[#A43790] text-white" : "neo-pressed text-[#4C5478]"}`}>
            {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
            <span>{voiceOn ? "Voz activada" : "Voz silenciada"}</span>
          </button>
        )}
      </div>

      <div className="neo-pressed rounded-2xl flex items-center gap-3 px-4 py-3" data-testid="hub-search-box">
        <Search size={18} className="text-[#6E7591] shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar aplicación… (usuarios, calendario, informes)"
          aria-label="Buscar aplicación"
          data-testid="hub-search"
          className="flex-1 min-w-0 bg-transparent outline-none text-sm font-bold text-[#1F2547] placeholder:text-[#8A91AB] !shadow-none !border-0"
        />
        {q ? (
          <button type="button" onClick={() => setQ("")} aria-label="Limpiar búsqueda" className="text-[#6E7591] hover:text-[#1F2547]"><X size={16} /></button>
        ) : (
          <kbd className="hidden sm:inline text-[10px] font-extrabold text-[#8A91AB] border border-[#C9D0E0] rounded px-1.5 py-0.5">/</kbd>
        )}
      </div>

      {q && totalFound === 0 && (
        <p className="text-center text-sm text-[#6E7591] py-6" data-testid="hub-search-empty">Ninguna aplicación coincide con «{q}».</p>
      )}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4 items-start" data-testid="hub-kanban">
        {groups.map((g) => {
          const GIcon = g.icon;
          return (
            <section key={g.id} className="neo-card p-4 sm:p-5 flex flex-col gap-4" data-testid={`hub-col-${g.id}`}>
              <header className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl grid place-items-center text-white shrink-0"
                  style={{ background: `linear-gradient(145deg, ${g.color}CC, ${g.color})`, boxShadow: `0 8px 16px -6px ${g.color}AA` }}>
                  <GIcon size={22} strokeWidth={2.2} />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="font-brand text-lg leading-tight" style={{ color: g.color }}>{g.label}</h2>
                  <p className="text-[11px] text-[#6E7591] leading-tight">{g.sub}</p>
                </div>
                <span className="neo-pressed text-[11px] font-extrabold rounded-full px-2.5 py-1 text-[#4C5478]">{g.items.length}</span>
              </header>
              <div className="h-1 rounded-full" style={{ background: `linear-gradient(90deg, ${g.color}, transparent)` }} />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3 gap-3">
                {g.items.map((it) => <AppTile key={it.testid} item={it} onSpeak={isStudent ? speakTile : null} />)}
              </div>
            </section>
          );
        })}
      </div>
      <p className="text-center text-[10px] font-bold tracking-wider uppercase text-[#8A91AB]" data-testid="hub-version">NEUROMATEMÁTICAS JASQ® · v{APP_VERSION}</p>
    </div>
  );
}
